import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Text, Textarea, useToast } from '@chakra-ui/react';
import type { MarketplaceDeliverableType, MarketplaceOpportunityListItemDto } from '@foodfluence/shared';
import { deliverableLabel } from '@foodfluence/shared';
import { lcApi } from '../../../services/api/lc';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';
import {
  AddDeliverableRow,
  DeliverablePickerSheet,
  DeliverableRows,
  EditableLine,
  linesToDeliverables,
  newLineId,
  toEditableLines,
} from './marketplaceDeliverableSheet';

export interface ApplyToOpportunitySheetProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: MarketplaceOpportunityListItemDto;
  onSuccess: () => void;
}

export function ApplyToOpportunitySheet({
  isOpen,
  onClose,
  opportunity,
  onSuccess,
}: ApplyToOpportunitySheetProps) {
  const toast = useToast();

  const [cash, setCash] = useState<number | ''>(opportunity.cash ?? 0);
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [rows, setRows] = useState<EditableLine[]>(() => toEditableLines(opportunity.deliverables));
  const [submitting, setSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCash(opportunity.cash ?? 0);
    setMessage('');
    setShowMessage(false);
    setRows(toEditableLines(opportunity.deliverables));
    setPickerOpen(false);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = (id: string, patch: Partial<EditableLine>) => {
    setRows((prev) => prev.map((r) => (r.id !== id ? r : { ...r, ...patch })));
  };

  const addRow = (type: MarketplaceDeliverableType) => {
    setRows((prev) => [...prev, { id: newLineId(), type, quantity: 1, carouselPhotos: 5 }]);
    setPickerOpen(false);
  };

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const canSubmit = typeof cash === 'number' && cash > 0 && rows.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (typeof cash !== 'number' || cash <= 0) return;
    setSubmitting(true);
    try {
      const proposedDeliverables = rows.length > 0 ? linesToDeliverables(rows) : undefined;
      await lcApi.applyToOpportunity(opportunity.id, {
        proposedCash: cash,
        ...(proposedDeliverables ? { proposedDeliverables } : {}),
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      toast({ title: 'Application submitted!', description: 'The brand will review your application.', status: 'success', duration: 4000, isClosable: true });
      onSuccess();
      onClose();
    } catch (err: any) {
      toast({ title: 'Could not apply', description: err?.message || 'Please try again.', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  const brandSummary = opportunity.orderBrief?.narrative?.trim() || opportunity.orderBrief?.projectName?.trim() || '';

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onClose={onClose}
        title={opportunity.brandName ?? 'Apply'}
      footer={
        // Sticky so the submit stays reachable while the offer rows grow.
        <Box
          position="sticky"
          bottom={0}
          mt={5}
          mx={-5}
          px={5}
          pt="14px"
          pb="env(safe-area-inset-bottom)"
          bg="white"
          borderTop={`1px solid ${ff.line}`}
        >
          <Button
            variant="lime"
            w="100%"
            onClick={handleSubmit}
            isDisabled={!canSubmit}
            isLoading={submitting}
            loadingText="Submitting…"
          >
            Submit application
          </Button>
        </Box>
        }
      >
        {brandSummary && (
          <Text fontSize="15px" color={ff.text2} mb={5} noOfLines={2} lineHeight="1.45">
            {brandSummary}
          </Text>
        )}

        <Text textStyle="eyebrow" color={ff.text2} mb="10px">
          Brand's ask
        </Text>
        <Box border="1px solid" borderColor={ff.line} borderRadius="20px" overflow="hidden" mb={5}>
          {toEditableLines(opportunity.deliverables).map((line) => (
            <Flex key={line.id} align="center" px={4} minH="48px" borderBottomWidth="1px" borderColor={ff.line}>
              <Text fontSize="15px" fontWeight="600" color={ff.muted} w="32px">{line.quantity}×</Text>
              <Text fontSize="15px" color={ff.body} flex={1}>{deliverableLabel(line.type)}</Text>
            </Flex>
          ))}
          <Flex align="center" px={4} minH="56px" bg={ff.limeSurface}>
            <Text fontSize="15px" color={ff.body} flex={1}>Budget</Text>
            <Text textStyle="display" fontSize="22px" color={ff.ink}>${opportunity.cash ?? 0}</Text>
          </Flex>
        </Box>

        <Text textStyle="eyebrow" color={ff.text2} mb="10px">
          Your offer
        </Text>
        <Box border="1px solid" borderColor={ff.line} borderRadius="20px" overflow="hidden">
          <DeliverableRows rows={rows} onUpdate={updateRow} onRemove={removeRow} />
          <AddDeliverableRow rows={rows} onOpen={() => setPickerOpen(true)} />

          {/* Price */}
          <Flex align="center" px={4} minH="56px" borderBottomWidth={showMessage ? '1px' : 0} borderColor={ff.line}>
            <Text fontSize="15px" fontWeight="600" color={ff.ink} flex={1}>Price</Text>
            <Text textStyle="display" fontSize="20px" color={ff.muted} mr={1}>$</Text>
            <Box
              as="input"
              type="number"
              min={1}
              value={cash === '' ? '' : cash}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = parseFloat(e.target.value);
                setCash(isNaN(v) ? '' : v);
              }}
              textAlign="right"
              fontFamily="display"
              fontSize="20px"
              fontWeight="600"
              color={ff.ink}
              bg="transparent"
              border="none"
              outline="none"
              w="96px"
              h="44px"
              placeholder="0"
              sx={{
                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                '&[type=number]': { MozAppearance: 'textfield' },
                '&::placeholder': { color: ff.muted },
              }}
            />
          </Flex>

          {/* Message */}
          {!showMessage ? (
            <Flex as="button" type="button" onClick={() => setShowMessage(true)} align="center" px={4} minH="52px" w="100%" textAlign="left" justify="space-between" _hover={{ bg: ff.grey }}>
              <Text fontSize="15px" fontWeight="600" color={ff.ink}>Add a message</Text>
              <Text fontSize="13px" color={ff.muted}>Optional</Text>
            </Flex>
          ) : (
            <Box px={4} pt={3} pb={3}>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell the brand why you're a great fit…"
                rows={3}
                fontSize="16px"
                resize="none"
              />
            </Box>
          )}
        </Box>
      </ModalShell>

      <DeliverablePickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        rows={rows}
        onPick={addRow}
      />
    </>
  );
}
